import { inject, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { UtilService } from '@services/util/util.service';
import { VwoService } from '@services/vwo/vwo.service';
import { VariablesService } from '@services/variables/variables.service';
import { EventsService } from '@services/events/events.service';
import { PaymentUtilService } from '@services/payment-util/payment-util.service';
import { CloseBtnComponent } from '@common-components/close-btn/close-btn.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { ComponentLoader } from 'src/app/core/component-loader/component-loader.component';
import { CurrencyIconComponent } from '@core-components/currency-icon/currency-icon.component';

interface ITip {
  percent: number;
  label: string;
}
@Component({
  selector: 'app-tip-card',
  standalone: true,
  imports: [
    NgFor,
    NgIf,
    NgTemplateOutlet,
    CurrencyIconComponent,
    CloseBtnComponent,
    ReactiveFormsModule,
    FormsModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatNativeDateModule,
    ComponentLoader
  ],
  templateUrl: './tip-card.component.html',
  styleUrls: ['./tip-card.component.scss']
})
export class TipCardComponent {

  public util = inject(UtilService);
  public sheet = inject(MatBottomSheet);
  public vwo = inject(VwoService);
  public vars = inject(VariablesService);
  private paymentUtil = inject(PaymentUtilService);
  private events = inject(EventsService);

  @Input() currency;
  @Input() v2;
  @Input() amount;
  @Input() baseAmount;
  @Output() lastTipAmtObj = new EventEmitter();
  @Output() tipAmount = new EventEmitter();
  @Output() tipPercent = new EventEmitter();

  selectedTip = 18;
  noTipDefault = 5;
  tips = [] as ITip[];
  tipAmt = 0;
  tipAmtToggle = 0;
  baseAmtToggle = 0;
  initialDefaultAmt = false;
  showToggleAmt = false;
  isToggleChecked = true;

  customAmountForm: FormGroup;
  customAmount: FormControl;

  previousVal = 0;
  showPopover;

  defaultTip5 = ['mission-oxygen-helping-hospitals-to-save-lives', 'dream11-for-india'];
  isMobile = this.vars.isMobile;

  ngOnChanges(changes) {
    // changes.baseAmount.currentValue
    // Removed baseAmount.currentValue condition as clearing other amount doesn't update total amount
    if ((changes && changes.baseAmount) || changes.amount || changes.currency) {
      this.selectTip({ value: this.selectedTip });
      if (changes?.baseAmount?.currentValue) {
        this.baseAmount = changes.baseAmount.currentValue;
      }
      if (!this.showToggleAmt || changes.currency || changes.baseAmount) {
        this.setTipArray();
      }
    }
  }

  ngOnInit() {
    this.initForm();
    if (this.defaultTip5.includes(this.vars.fundraiser.custom_tag)) {
      this.selectedTip = 5;
    }
    this.paymentUtil.paymentCurrency.subscribe(res => {
      this.currency = res;
      if (this.vars.pageName === 'stories') {
        if (this.currency !== 'INR') {
          this.selectedTip = 10;
          this.selectTip({ value: this.selectedTip });
        } else {
          this.selectedTip = 18;
          if (this.vwo.slab_changes_tip_AB()) {
            this.selectedTip = 20;
          }
          this.selectTip({ value: this.selectedTip });
        }
      }
    });
    //for AB test of tip slab amount
    if ((this.vars.pageName === 'stories' || this.vars.fundraiser.parent_cause_id === 20) && this.vwo.slab_changes_tip_AB()) {
      this.selectedTip = 20;
      this.selectTip({ value: this.selectedTip });
    }
    setTimeout(() => {
      this.selectTip({value: this.selectedTip});
      if (!this.showToggleAmt) {
        this.setTipArray();
      }
    }, 100);
  }

  initForm() {
    this.customAmountForm = new FormGroup({
      customAmount: this.customAmount = new FormControl(0, [Validators.required, Validators.min(0)]),
    });
    this.customAmount.valueChanges.subscribe(val => {
      if (val && !this.vars.numberOnlyRegex.test(val) && this.tipAmt !== +val) {
        this.customAmount.setValue(this.tipAmt||0);
      }
    });
  }

  selectTip(_event, native?) {
    this.selectedTip = native ? +_event.target.value : +_event.value;
    this.vars.selectTipPercentage = this.selectedTip;
    if (this.selectedTip === 0) {
      this.tipAmount.emit(this.tipAmt ? this.tipAmt : null);
      this.tipPercent.emit('other ' + ((this.tipAmt * 100) / this.baseAmount).toFixed(2));
      return false;
    }
    this.tipAmount.emit(this.calculateTIp(this.selectedTip));
    this.tipPercent.emit(this.selectedTip);
  }

  calculateTIp(tip) {
    return Math.round((this.baseAmount * tip) / 100);
  }

  openTipInfo(template) {
    this.sheet.open(template, {
      panelClass: 'bottom-sheet-container'
    });
  }

  /**Set the tip amount */
  setTipArray() {
    this.tips = [];
    let data = [18, 10, 5];
    //for AB test of tip slab amount
    if ((this.vars.pageName === 'stories' || this.vars.fundraiser.parent_cause_id === 20) && this.vwo.slab_changes_tip_AB()) {
      data = [20, 15, 10];
    }

    if (!this.selectedTip) {
      this.setOtherAmount(data);
    }

    for (const item of data) {
      this.tips.push({
        percent: item,
        label: `(${this.currency} ${this.calculateTIp(item)})`
      });
    }
    this.tips.push({
      label: `Other`,
      percent: 0,
    });
    // }
  }

  setOtherAmount(data) {
    // Set default value for other option!
    const defaultOtherValue = this.calculateTIp(this.selectedTip ? this.selectedTip : data[2]);

    // Logic to check whether the user has changed the default set tip amout , if he has not to change it again everytime except intial
    if (this.customAmountForm && (this.tipAmt === this.previousVal || !this.initialDefaultAmt)) {
      this.initialDefaultAmt = true;
      this.customAmountForm.controls['customAmount'].setValue(defaultOtherValue || 0);
      this.previousVal = defaultOtherValue;
      this.tipAmt = defaultOtherValue;
      this.tipAmount.emit(defaultOtherValue);
    } else if (this.showToggleAmt) {
      this.getTipFromBaseAmt(this.isToggleChecked);
    }
  }


  // Input of custom tip amount
  onInput() {
    const tipValue = this.customAmount.value ? this.customAmount.value : 0;
    if (!tipValue) {
      this.customAmount.setValue(tipValue);
    } else if (tipValue !== 0) {
      this.customAmountForm.reset();
      this['customAmount'].setValue(tipValue);
    }
    this.tipAmt = tipValue;  // Maintain tip value for change in basic amount
    this.tipAmount.emit(tipValue);
  }

  toggle(event): void {
    this.isToggleChecked = event.checked;
    this.getTipFromBaseAmt(this.isToggleChecked);
  }

  getTipFromBaseAmt(isChecked = true) {
    setTimeout(() => {
      this.tipAmtToggle = isChecked ? Math.round(this.noTipDefault / 100 * this.baseAmount) : 0;
      this.baseAmtToggle = this.baseAmount - this.tipAmtToggle;
      this.lastTipAmtObj.emit({ baseAmt: this.baseAmtToggle, tipAmt: this.tipAmtToggle });
      this.showToggleAmt = true;
    }, 100);
  }

  addExtraTip = (amt) => {
    this.selectTip({value: 0});
    setTimeout(() => {
      this.customAmount.setValue(amt);
      this.onInput();
    }, 100);
  }

  triggerOtherTipEvent(_event, native?) {
    const selectedTip = native ? +_event.target.value : +_event.value;
    if (selectedTip !== 0) return;
    this.events.sendSystemEvent({eventName: 'other tip selected'}).subscribe(_=>_);
  }
}
